/**
 * Markdown preview component with syntax highlighting
 * Renders markdown content with proper styling and code highlighting
 */

import { useEffect, useState, useRef } from 'react'
import 'prismjs/themes/prism.css'
import { useImageRefreshDelegation } from '@/components/ui/refreshable-image'

interface MarkdownPreviewProps {
  content: string
  className?: string
}

/**
 * Filter out "think:" prefixed content from LLM responses
 * This internal reasoning should not be displayed to users
 */
function filterThinkingContent(text: string): string {
  if (!text) return text;

  const trimmed = text.trim();

  // If the entire content starts with "think:", filter it completely
  if (/^think:/i.test(trimmed)) {
    // Extract any real content after the thinking block
    // Look for patterns that indicate end of thinking: headers, bullets, numbered lists
    const realContentMatch = trimmed.match(/(?:^|\n)(#{1,6}\s|[-*•]\s|\d+\.\s|[A-Z][a-z]+:)/m);
    if (realContentMatch && realContentMatch.index !== undefined) {
      const realContent = trimmed.substring(realContentMatch.index).trim();
      if (realContent && !/^think:/i.test(realContent)) {
        return realContent;
      }
    }
    // No real content found - this is pure thinking content
    return '';
  }

  // Filter out any "think:" blocks embedded in the content
  // Split by newlines and filter lines that look like thinking
  const lines = text.split('\n');
  let inThinkingBlock = false;
  const filteredLines: string[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Start of thinking block
    if (/^think:/i.test(trimmedLine)) {
      inThinkingBlock = true;
      continue;
    }

    // End of thinking block (real content indicators)
    if (inThinkingBlock) {
      if (/^(#{1,6}\s|[-*•]\s|\d+\.\s|[A-Z][a-z]+:|```|<)/.test(trimmedLine)) {
        inThinkingBlock = false;
      } else {
        continue; // Still in thinking block, skip this line
      }
    }

    filteredLines.push(line);
  }

  return filteredLines.join('\n');
}

export function MarkdownPreview({ content, className = '' }: MarkdownPreviewProps) {
  const [html, setHtml] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { handleImageError } = useImageRefreshDelegation(containerRef)

  useEffect(() => {
    const processMarkdown = async () => {
      try {
        setIsLoading(true)

        // Filter out "think:" content before processing
        const filteredContent = filterThinkingContent(content);

        // If all content was thinking content, show nothing
        if (!filteredContent.trim()) {
          setHtml('');
          setIsLoading(false);
          return;
        }

        // Debug logging only when thinking content was filtered
        if (filteredContent.length !== content.length) {
          console.log('🎨 MarkdownPreview - Filtered out thinking content:', content.length - filteredContent.length, 'chars');
        }
        
        // Dynamic imports to reduce bundle size
        const [{ default: MarkdownIt }, { default: Prism }] = await Promise.all([
          import('markdown-it'),
          import('prismjs')
        ])

        const md = new MarkdownIt({
          html: true,        // Enable HTML tags in source
          xhtmlOut: true,    // Use '/' to close single tags (<br />)
          breaks: true,      // Convert '\n' in paragraphs into <br>
          linkify: true,     // Autoconvert URL-like text to links
          typographer: true, // Enable some language-neutral replacement + quotes beautification
          quotes: '""\'\'',
        })

        // Language loading cache to avoid multiple imports
        const loadedLanguages = new Set(['markup', 'css', 'javascript'])
        
        // Language mapping for common aliases
        const languageMap: Record<string, string> = {
          'js': 'javascript',
          'ts': 'typescript',
          'jsx': 'jsx',
          'tsx': 'tsx',
          'py': 'python',
          'rb': 'ruby',
          'sh': 'bash',
          'shell': 'bash',
          'yml': 'yaml',
          'md': 'markdown',
          'cpp': 'cpp',
          'c++': 'cpp',
          'cs': 'csharp',
          'php': 'php',
          'go': 'go',
          'rs': 'rust',
          'java': 'java',
          'kt': 'kotlin',
          'swift': 'swift',
          'sql': 'sql',
          'json': 'json',
          'xml': 'xml',
          'html': 'markup',
          'scss': 'scss',
          'sass': 'sass',
          'less': 'less'
        }

        // Function to dynamically load Prism language
        const loadLanguage = async (lang: string): Promise<boolean> => {
          if (loadedLanguages.has(lang)) return true
          
          try {
            await import(/* @vite-ignore */ `prismjs/components/prism-${lang}`)
            loadedLanguages.add(lang)
            return true
          } catch (error) {
            console.warn(`Failed to load Prism language: ${lang}`, error)
            return false
          }
        }

        // Override fence renderer for syntax highlighting
        md.renderer.rules.fence = function(tokens, idx) {
          const token = tokens[idx]
          const info = token.info ? token.info.trim() : ''
          const langName = info ? info.split(/\s+/g)[0] : ''
          const mappedLang = languageMap[langName] || langName
          
          if (mappedLang && Prism.languages[mappedLang]) {
            try {
              const highlighted = Prism.highlight(token.content, Prism.languages[mappedLang], mappedLang)
              return `<pre class="language-${mappedLang}"><code class="language-${mappedLang}">${highlighted}</code></pre>\n`
            } catch (error) {
              console.warn('Prism highlighting failed for language:', mappedLang, error)
            }
          }
          
          // Fallback to plain code block
          return `<pre><code class="language-${langName || 'text'}">${md.utils.escapeHtml(token.content)}</code></pre>\n`
        }

        // Override code_block renderer
        md.renderer.rules.code_block = function(tokens, idx) {
          const token = tokens[idx]
          return `<pre><code>${md.utils.escapeHtml(token.content)}</code></pre>\n`
        }

        // Process markdown
        let rendered = md.render(filteredContent)

        // Post-process to add color chips for hex colors in code elements
        rendered = rendered.replace(/<code([^>]*)>(.*?)<\/code>/g, (match, attributes, codeContent) => {
          // Check if the code content contains hex colors
          const hexColorRegex = /#[A-Fa-f0-9]{6}/g
          if (hexColorRegex.test(codeContent)) {
            // Replace hex colors with color chips
            const processedContent = codeContent.replace(hexColorRegex, (hexColor: string) => {
              return `<span class="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-muted/30 border not-prose">
                <div class="w-3 h-3 rounded-full border border-white shadow-sm ring-1 ring-black/10" style="background-color: ${hexColor}"></div>
                <span class="text-xs font-mono">${hexColor}</span>
              </span>`
            })
            return `<code${attributes}>${processedContent}</code>`
          }
          return match
        })
        
        setHtml(rendered)

        // Load languages found in the content and re-highlight
        const codeBlocks = rendered.match(/class="language-(\w+)"/g)
        if (codeBlocks) {
          const languages = [...new Set(
            codeBlocks.map(match => match.match(/language-(\w+)/)?.[1]).filter(Boolean)
          )] as string[]
          
          // Load all required languages
          await Promise.allSettled(
            languages.map(lang => loadLanguage(languageMap[lang] || lang))
          )
          
          // Re-render with loaded languages
          let reRendered = md.render(filteredContent)
          
          // Post-process to add color chips for hex colors in code elements
          reRendered = reRendered.replace(/<code([^>]*)>(.*?)<\/code>/g, (match, attributes, codeContent) => {
            // Check if the code content contains hex colors
            const hexColorRegex = /#[A-Fa-f0-9]{6}/g
            if (hexColorRegex.test(codeContent)) {
              // Replace hex colors with color chips
              const processedContent = codeContent.replace(hexColorRegex, (hexColor: string) => {
                return `<span class="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-muted/30 border not-prose">
                  <div class="w-3 h-3 rounded-full border border-white shadow-sm ring-1 ring-black/10" style="background-color: ${hexColor}"></div>
                  <span class="text-xs font-mono">${hexColor}</span>
                </span>`
              })
              return `<code${attributes}>${processedContent}</code>`
            }
            return match
          })
          
          setHtml(reRendered)
        }
        
      } catch (error) {
        console.error('Error processing markdown:', error)
        setHtml(`<p>Error processing markdown content</p>`)
      } finally {
        setIsLoading(false)
      }
    }

    processMarkdown()
  }, [content])

  // Apply syntax highlighting after component updates
  useEffect(() => {
    if (html && !isLoading) {
      const highlightCode = async () => {
        try {
          const { default: Prism } = await import('prismjs')
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            Prism.highlightAll()
          })
        } catch (error) {
          console.warn('Failed to apply syntax highlighting:', error)
        }
      }
      
      highlightCode()
    }
  }, [html, isLoading])

  // Attach error delegation for signed URL refresh on images
  useEffect(() => {
    const el = containerRef.current
    if (!el || !html || isLoading) return
    el.addEventListener('error', handleImageError, true) // capture phase for <img>
    return () => el.removeEventListener('error', handleImageError, true)
  }, [html, isLoading, handleImageError])

  if (isLoading) {
    return (
      <div className={`markdown-preview-loading ${className}`}>
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">
            <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full"></div>
            <p className="mt-2 text-sm">Rendering markdown...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`markdown-preview prose prose-sm min-w-0 max-w-full dark:prose-invert ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

// Additional CSS styles for markdown content
const markdownStyles = `
.markdown-preview {
  line-height: 1.6;
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.markdown-preview.prose {
  max-width: none !important;
}

.markdown-preview h1,
.markdown-preview h2,
.markdown-preview h3,
.markdown-preview h4,
.markdown-preview h5,
.markdown-preview h6 {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: 600;
}

.markdown-preview h1 {
  font-size: 1.875rem;
  border-bottom: 2px solid;
  border-bottom-color: rgba(156, 163, 175, 0.2);
  padding-bottom: 0.5rem;
}

.markdown-preview h2 {
  font-size: 1.5rem;
  border-bottom: 1px solid;
  border-bottom-color: rgba(156, 163, 175, 0.2);
  padding-bottom: 0.25rem;
}

.markdown-preview h3 {
  font-size: 1.25rem;
}

.markdown-preview h4 {
  font-size: 1.125rem;
}

.markdown-preview p {
  margin-bottom: 1em;
}

.markdown-preview ul,
.markdown-preview ol {
  margin-bottom: 1em;
  padding-left: 1.5rem;
}

.markdown-preview li {
  margin-bottom: 0.25em;
}

.markdown-preview blockquote {
  border-left: 4px solid currentColor;
  border-left-color: rgba(156, 163, 175, 0.5);
  padding-left: 1rem;
  margin: 1em 0;
  font-style: italic;
  opacity: 0.8;
}

.markdown-preview code {
  background-color: rgba(156, 163, 175, 0.1);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.markdown-preview pre {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  margin: 1em 0;
}

/* Override Prism CSS for proper width constraints */
.markdown-preview pre[class*="language-"] {
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: auto !important;
  white-space: pre !important;
  box-sizing: border-box !important;
}

.markdown-preview pre[class*="language-"] code {
  display: block;
  width: 100%;
  max-width: 100%;
}

.dark .markdown-preview pre {
  background-color: rgba(255, 255, 255, 0.05);
}

.markdown-preview pre code {
  background-color: transparent;
  padding: 0;
}

.markdown-preview table {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
}

.markdown-preview th,
.markdown-preview td {
  border: 1px solid currentColor;
  border-color: rgba(156, 163, 175, 0.3);
  padding: 0.5rem;
  text-align: left;
}

.markdown-preview th {
  background-color: rgba(156, 163, 175, 0.1);
  font-weight: 600;
}

.markdown-preview a {
  color: rgb(59, 130, 246);
  text-decoration: underline;
}

.markdown-preview a:hover {
  color: rgb(29, 78, 216);
}

.dark .markdown-preview a {
  color: rgb(96, 165, 250);
}

.dark .markdown-preview a:hover {
  color: rgb(147, 197, 253);
}

.markdown-preview img {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.markdown-preview audio,
.markdown-preview video {
  max-width: 100%;
  margin: 1rem 0;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.markdown-preview video {
  height: auto;
}

.markdown-preview hr {
  border: none;
  border-top: 1px solid;
  border-top-color: rgba(156, 163, 175, 0.2);
  margin: 2em 0;
}

/* Task list styling */
.markdown-preview ul.contains-task-list {
  list-style: none;
  padding-left: 0;
}

.markdown-preview .task-list-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.markdown-preview .task-list-item input[type="checkbox"] {
  margin-top: 0.125rem;
}
`

// Inject styles into the document head
if (typeof document !== 'undefined') {
  let styleElement = document.head.querySelector('style[data-markdown-preview]')
  if (!styleElement) {
    styleElement = document.createElement('style')
    styleElement.setAttribute('data-markdown-preview', 'true')
    document.head.appendChild(styleElement)
  }
  styleElement.textContent = markdownStyles
}